import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Shield, Users, FileText, Settings, LogOut, ArrowLeft,
  Loader2, Trash2, Star, Tag, MessageSquare
} from 'lucide-react';
import api from '../utils/api';

const AdminReviews = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => { fetchReviews(); }, []);

  const fetchReviews = async () => {
    try {
      const data = await api.admin.getReviews();
      setReviews(data.reviews || []);
    } catch (err) {
      console.error('Failed to fetch reviews:', err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this review?')) return;
    try {
      await api.admin.deleteReview(id);
      setReviews(prev => prev.filter(r => r._id !== id));
      setMessage({ type: 'success', text: 'Review deleted.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isAdmin');
    navigate('/admin-portal-7x9k2m');
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star key={i} className={`h-4 w-4 ${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}`} />
    ));
  };

  const avgRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : '—';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-red-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-600 rounded-lg">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Admin Portal</h1>
              <p className="text-xs text-gray-400">Due Diligence Platform</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white transition-colors">
              <ArrowLeft className="h-4 w-4" />
              User Portal
            </Link>
            <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white transition-colors">
              <LogOut className="h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <nav className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1 py-2">
            {[
              { to: '/admin-portal-7x9k2m/dashboard', icon: Shield, label: 'Dashboard' },
              { to: '/admin-portal-7x9k2m/reports', icon: FileText, label: 'Reports' },
              { to: '/admin-portal-7x9k2m/users', icon: Users, label: 'Users' },
              { to: '/admin-portal-7x9k2m/config', icon: Settings, label: 'Config' },
              { to: '/admin-portal-7x9k2m/vouchers', icon: Tag, label: 'Vouchers' },
              { to: '/admin-portal-7x9k2m/reviews', icon: Star, label: 'Reviews' },
              { to: '/admin-portal-7x9k2m/audit', icon: Shield, label: 'Audit' },
              { to: '/admin-portal-7x9k2m/api-keys', icon: Shield, label: 'API Keys' },
            ].map(item => (
              <Link key={item.to} to={item.to}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                  item.label === 'Reviews' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                }`}>
                <item.icon className="h-4 w-4" />{item.label}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {message.text && (
          <div className={`mb-4 p-3 rounded-md text-sm ${message.type === 'success' ? 'bg-green-900/50 text-green-300 border border-green-700' : 'bg-red-900/50 text-red-300 border border-red-700'}`}>
            {message.text}
          </div>
        )}

        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">User Reviews</h2>
          <div className="flex items-center gap-4 text-sm text-gray-400">
            <span>{reviews.length} review{reviews.length !== 1 ? 's' : ''}</span>
            <span className="flex items-center gap-1">
              <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
              {avgRating} avg
            </span>
          </div>
        </div>

        <div className="space-y-4">
          {reviews.length === 0 ? (
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 text-center text-gray-500">
              <MessageSquare className="h-8 w-8 mx-auto mb-3 opacity-50" />
              No reviews yet. Reviews are collected after report downloads.
            </div>
          ) : reviews.map(review => (
            <div key={review._id} className="bg-gray-800 border border-gray-700 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-white font-medium">
                      {review.userId?.name || review.userId?.email || review.userName}
                    </span>
                    <div className="flex gap-0.5">{renderStars(review.rating)}</div>
                    <span className="text-xs text-gray-500">
                      Report: {review.reportId}
                    </span>
                  </div>
                  {review.reviewText && (
                    <p className="text-gray-300 text-sm">{review.reviewText}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    {new Date(review.createdAt).toLocaleString()}
                  </p>
                </div>
                <button onClick={() => handleDelete(review._id)}
                  className="text-red-400 hover:text-red-300 ml-4">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default AdminReviews;
